// run-command-prompt-builder.ts
//
// `buildPrompt(ticket)` for `avatar autopilot run` (P9b) — the other missing
// production seam alongside run-command-session-deps.ts. Renders the ticket
// into the `session-prompt.tpl.md` shape (spec §6.2 "Nội dung prompt"):
// ticket revision + allowed_paths + playbook references + output contract
// summary. `run_id`/`session_id` are DELIBERATELY absent here —
// session-runner.ts writes those into the binding file AFTER
// buildPrompt already ran (supervisor-loop.ts calls `buildPrompt(ticket)`
// before runId/sessionId/stagingDir exist — verified against the call site),
// exactly matching the template's own instruction that the worker reads
// those "nguyên văn từ binding", never from the prompt text.
//
// Playbooks: `workflows/autopilot/principles.md` is ALWAYS required (missing
// ⇒ contract_error, no spawn). `workflows/autopilot/<kind>.md` is loaded when
// present, else that section is omitted ("Mỗi mục render RỖNG... nếu adapter
// không cần"); `review-clean-v1` gets `review-clean.md` instead (C2 below).

import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists } from "../filesystem-helpers.js";
import {
  ADAPTER_PLAYBOOK_RELATIVE_PATHS,
  kindPlaybookRelativePath,
} from "./adapter-specific-playbook-paths.js";
import { COMMAND_ADAPTER_KINDS, resolveCommandAdapter } from "./command-adapter.js";
import { EVIDENCE_REQUIRED_ADAPTER_IDS } from "./command-adapter-table.js";
import { REVIEW_REPORT_REQUIRED_ADAPTER_IDS } from "./review-report-promotion-wiring.js";
import { decidedQuestionsPromptLines } from "./run-command-prompt-decided-questions-section.js";
import { renderOutputContractSection } from "./run-command-prompt-output-contract-section.js";
import { renderTicketContextSection } from "./run-command-prompt-ticket-context-section.js";
import type { Ticket } from "./schemas/ticket-schema.js";

export class PromptBuildContractError extends Error {
  readonly code = "contract_error";
  constructor(message: string) {
    super(message);
    this.name = "PromptBuildContractError";
  }
}

export const PRINCIPLES_RELATIVE_PATH = join("workflows", "autopilot", "principles.md");
// A1 (plan 20260915-1650 phase-01): evidence discipline (spec §A1 — status
// translation table, six bans, self-scan, "ask the schema"). Inlined like
// principles because this builder is the ONLY thing a headless worker reads
// (`session-prompt.tpl.md` documents the shape; it is never loaded). OPTIONAL:
// a pack pinned before this phase, and every e2e fixture pack, lacks the
// file — the section is simply omitted, nothing else changes.
const EVIDENCE_DISCIPLINE_RELATIVE_PATH = join("workflows", "autopilot", "evidence-discipline.md");

/**
 * C2: the adapter whose prompt is deliberately starved of the implementing
 * session's context. Kept as a literal rather than imported from the table so
 * this module keeps depending on ids only, matching how it already treats
 * EVIDENCE_REQUIRED_ADAPTER_IDS.
 */
const CLEAN_REVIEW_ADAPTER_ID = "review-clean-v1";
// Adapter-specific playbooks: adapter-specific-playbook-paths.ts (optional).

async function readPackFileOrUndefined(
  packRoot: string,
  relativePath: string,
): Promise<string | undefined> {
  const absolute = join(packRoot, relativePath);
  if (!(await pathExists(absolute))) return undefined;
  return fs.readFile(absolute, "utf8");
}

/**
 * Builds `buildPrompt(ticket)` bound to one mission's pack root (the mission
 * workspace's OWN `.claude/pack`, never the source workspace's pack, so a
 * mission stays reproducible even if the operator's workspace pack is later
 * resynced to a different version).
 *
 * ASYNC FACTORY, SYNC RESULT: `supervisor-loop.ts`'s `buildPrompt` field is
 * typed `(ticket: Ticket) => string` (synchronous — called mid-tick with no
 * `await`, verified against the call site). Playbook content only varies by
 * `kind` (a fixed 10-value enum), so every playbook file is read ONCE here
 * (before dispatch) and the closure is a synchronous lookup. The one read per
 * ticket is `controlRoot`'s interview.json (V2-0) — answers land mid-run.
 */
export async function makeRunCommandPromptBuilder(
  packRoot: string,
  missionWorkspace?: string, // P-1: absolute binding path in the prompt
  controlRoot?: string, // V2-0: where the human's answers live (absent ⇒ no section)
): Promise<(ticket: Ticket) => string> {
  const principles = await readPackFileOrUndefined(packRoot, PRINCIPLES_RELATIVE_PATH);
  if (principles === undefined) {
    throw new PromptBuildContractError(
      `run-command-prompt-builder: thiếu playbook bắt buộc "${PRINCIPLES_RELATIVE_PATH}" trong pack ${packRoot} — không spawn.`,
    );
  }

  const evidenceDiscipline = await readPackFileOrUndefined(
    packRoot,
    EVIDENCE_DISCIPLINE_RELATIVE_PATH,
  );

  const kindPlaybooks = new Map<string, string>();
  for (const kind of COMMAND_ADAPTER_KINDS) {
    const content = await readPackFileOrUndefined(packRoot, kindPlaybookRelativePath(kind));
    if (content !== undefined) kindPlaybooks.set(kind, content);
  }
  const adapterPlaybooks = new Map<string, string | undefined>();
  for (const [id, path] of Object.entries(ADAPTER_PLAYBOOK_RELATIVE_PATHS)) {
    adapterPlaybooks.set(id, await readPackFileOrUndefined(packRoot, path));
  }

  return (ticket: Ticket): string => {
    // Validate the adapter resolves — an unknown command_adapter is a
    // contract_error (mirrors scheduler.ts's buildDispatchSpec, which
    // session-runner never reaches for an invalid adapter either; this
    // check runs BEFORE that one since buildPrompt is called first).
    let adapterId: string;
    try {
      adapterId = resolveCommandAdapter(ticket.command_adapter).id;
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      throw new PromptBuildContractError(
        `run-command-prompt-builder: ticket ${ticket.id} command_adapter "${ticket.command_adapter}" không hợp lệ: ${detail}`,
      );
    }

    // C2 (plan 20260915-1650 phase-03): the clean reviewer's prompt is
    // defined by what it LACKS. `review-local-v1` sees plan_path, phase_path
    // and the `review` kind playbook — all of which carry the implementing
    // session's own framing (the playbook names the decision log outright).
    // A second reviewer reading the author's framing re-derives the author's
    // conclusions, which is the failure this adapter exists to avoid. So for
    // this id: no paths, `review-clean.md` instead of the kind playbook, and
    // the ticket's CONTRACT fields (outcome/acceptance/coverage_limits).
    const cleanReviewContext = adapterId === CLEAN_REVIEW_ADAPTER_ID;
    const adapterPlaybookPath = ADAPTER_PLAYBOOK_RELATIVE_PATHS[adapterId];
    const playbook =
      adapterPlaybookPath !== undefined
        ? { path: adapterPlaybookPath, content: adapterPlaybooks.get(adapterId) }
        : { path: kindPlaybookRelativePath(ticket.kind), content: kindPlaybooks.get(ticket.kind) };
    // The verifier rejects a reviewer result without `review_report`
    // (result-verifier-checks.ts) — the contract section must say so.
    const reviewReportRequired = REVIEW_REPORT_REQUIRED_ADAPTER_IDS.includes(adapterId);
    // N-05 (dogfood worker thật 2026-09-15): bootstrap tickets name a
    // `verification_recipe` ("survey-bootstrap") that exists in no profile;
    // echoing it into the example as a recipe_id sent a real survey worker
    // chasing a recipe it could never run. Adapters that run no recipe
    // (audit F2's EVIDENCE_REQUIRED set is the one source of truth) get an
    // empty example plus one explicit line instead.
    const evidenceRequired = EVIDENCE_REQUIRED_ADAPTER_IDS.includes(adapterId);

    const lines: string[] = [];
    lines.push(`# Autopilot session — ${ticket.kind} · ${adapterId}`);
    lines.push("");
    lines.push("## Ticket revision");
    lines.push(`- ticket_id: ${ticket.id} · revision: ${ticket.revision}`);
    lines.push("- Nội dung ticket (JSON, nguồn sự thật — không tự diễn giải thêm):");
    lines.push("```json");
    // C2: the clean reviewer's ticket JSON is redacted like its "Ticket
    // context" section (gating only the prose left the paths here — measured by
    // `test_review_clean_adapter_prompt_excludes_plan_and_phase_paths`). The
    // rest (id, revision, allowed_paths, C1 contract fields) is the contract.
    const ticketForPrompt = cleanReviewContext
      ? (({ plan_path: _planPath, phase_path: _phasePath, ...rest }) => rest)(ticket)
      : ticket;
    lines.push(JSON.stringify(ticketForPrompt, null, 2));
    lines.push("```");
    lines.push("");
    lines.push(...renderTicketContextSection(ticket, cleanReviewContext));
    lines.push(...decidedQuestionsPromptLines(controlRoot, ticket.id));
    lines.push("");
    lines.push("## Playbook cần thiết");
    lines.push(`- ${PRINCIPLES_RELATIVE_PATH} (luôn nạp — 10 nguyên tắc):`);
    lines.push(principles.trim());
    if (playbook.content !== undefined) {
      lines.push("");
      lines.push(`- ${playbook.path}:`);
      lines.push(playbook.content.trim());
    }
    if (evidenceDiscipline !== undefined) {
      // Placed BEFORE the output contract on purpose: the worker reads how
      // to phrase what it observed before it reads the fields it must fill.
      lines.push("");
      lines.push(
        `## Kỷ luật bằng chứng (${EVIDENCE_DISCIPLINE_RELATIVE_PATH} — luôn nạp, áp cho summary/uncovered_areas/remaining_work)`,
      );
      lines.push(evidenceDiscipline.trim());
    }
    lines.push(
      ...renderOutputContractSection(
        ticket,
        adapterId,
        evidenceRequired,
        reviewReportRequired,
        missionWorkspace,
      ),
    );

    return lines.join("\n");
  };
}
